import { socialApiClient } from '@/shared/api-client/social-api-client';

import {
  addPostsToBoardRequestSchema,
  boardSchema,
  boardsResponseSchema,
  createBoardRequestSchema,
  postsInBoardRequestSchema,
  removePostsFromBoardRequestSchema,
  updateBoardTitleRequestSchema,
} from '../board-schema';
import type { Board, PostsInBoard, PostsInBoardParams } from '../board-type';

export async function FetchBoards(workspaceId: string, signal?: AbortSignal): Promise<Board[]> {
  const response = await socialApiClient.get(`workspaces/${workspaceId}/boards`, { signal });
  return boardsResponseSchema.parse(await response.json());
}

export async function FetchPostsInBoard(
  boardId: string,
  params: PostsInBoardParams | null,
  signal?: AbortSignal,
): Promise<PostsInBoard> {
  const parsedRequest = postsInBoardRequestSchema.parse({
    page: params?.page ?? 1,
    limit: params?.limit ?? 20,
  });
  const response = await socialApiClient.get(`boards/${boardId}/posts`, {
    searchParams: parsedRequest,
    signal,
  });

  return await response.json<PostsInBoard>();
}

export async function CreateBoard(
  workspaceId: string,
  request: { title: string },
  signal?: AbortSignal,
): Promise<Board> {
  const parsedRequest = createBoardRequestSchema.parse(request);
  const response = await socialApiClient.post(`workspaces/${workspaceId}/board`, {
    json: parsedRequest,
    signal,
  });
  return boardSchema.parse(await response.json());
}

export async function DeleteBoard(boardId: string, signal?: AbortSignal): Promise<void> {
  await socialApiClient.delete(`boards/${boardId}`, { signal });
}

export async function UpdateBoardTitle(
  boardId: string,
  request: { title: string },
  signal?: AbortSignal,
): Promise<void> {
  const parsedRequest = updateBoardTitleRequestSchema.parse(request);
  await socialApiClient.patch(`boards/${boardId}/title`, {
    json: parsedRequest,
    signal,
  });
}

export async function AddPostsToBoard(
  boardId: string,
  request: { post_ids: string[] },
  signal?: AbortSignal,
): Promise<void> {
  const parsedRequest = addPostsToBoardRequestSchema.parse(request);
  await socialApiClient.post(`boards/${boardId}`, {
    json: parsedRequest,
    signal,
  });
}

export async function RemovePostsFromBoard(
  boardId: string,
  request: { post_ids: string[] },
  signal?: AbortSignal,
): Promise<void> {
  const parsedRequest = removePostsFromBoardRequestSchema.parse(request);
  await socialApiClient.patch(`boards/${boardId}`, {
    json: parsedRequest,
    signal,
  });
}

export async function ExportPostsInBoard(boardId: string, signal?: AbortSignal): Promise<string> {
  const response = await socialApiClient.post(`boards/${boardId}/posts`, {
    signal,
  });
  return response.text();
}
